// Barra de navegación inferior de mobile —
// ADR-2026-07-31-home-mobile-hero-nav-sheet-generar, extendida a todo el shell por
// ADR-2026-08-04-la-barra-inferior-vive-en-el-shell.
//
// Cinco lugares: cuatro destinos y, en el medio, un botón de ACCIÓN que abre el
// sheet de "Generar" (no navega a ninguna parte). Ese botón es el centro de la
// pantalla: sale del círculo elevado del diseño, por encima del borde de la barra.
//
// Nació en la home y hoy la monta el shell (`ShellMobile`), así que se ve en TODAS
// las pantallas del rediseño y no sólo en el inicio. `MobileBottomNav`, la barra
// vieja, quedó para las pantallas que siguen bajo `DashboardLayout` a secas
// (`/modos` y el landing de Estudio).

import { NavLink } from 'react-router-dom';
import { useSidebar } from '../../../context/SidebarContext';

type Tab = {
  to: string;
  label: string;
  /** Glifo de Material Symbols Rounded (la familia ya está cargada en `index.html`). */
  icono: string;
};

/**
 * Lo que ocupa la barra, para que una pantalla pueda dejarle el lugar libre —
 * ADR-2026-08-04-el-modo-se-lleva-la-barra.
 *
 * Son los 64px de la fila (`h-16`) más el safe area de abajo, o sea lo mismo que la
 * barra reserva para sí. Se exporta como string de `calc` y no como número porque el
 * safe area sólo lo conoce el navegador; quien necesite el hueco lo mete en su
 * `padding-bottom` tal cual.
 */
export const ALTO_NAV_INFERIOR = 'calc(4rem + env(safe-area-inset-bottom, 0px))';

// Los cuatro destinos, en el orden del diseño. Son rutas que YA existen: la barra
// no inventa pantallas, sólo cambia cómo se llega.
//   · Plantillas y Creaciones son las mismas que enlaza la home de desktop.
//   · Marca es `/mi-marca`, el mismo destino —y el mismo glifo— que el tercer ítem de
//     la sidebar de desktop. Antes ahí estaba Perfil (`/configuracion`); la cuenta se
//     sigue tocando desde el menú del avatar, y lo que se edita seguido es la marca.
const TAB_INICIO: Tab = { to: '/interfaz-v2', label: 'Inicio', icono: 'home' };

/**
 * El segundo lugar es Plantillas sólo para Moda; para el resto de los rubros va
 * Catálogo — ADR-2026-08-04-el-segundo-lugar-de-la-barra-sigue-al-rubro.
 *
 * El lugar no se deja vacío ni se colapsa la barra a cuatro: son cinco lugares de
 * ancho fijo y sacar uno recentra los otros cuatro, con lo que el botón de Generar
 * deja de estar en el medio. Catálogo es el reemplazo porque es el modo más cercano
 * a lo que ofrecía Plantillas —varias imágenes del mismo producto en una tanda— y
 * está disponible para todos los rubros.
 *
 * El nombre y el glifo salen de `contenidoAtlas` (`Catálogo` / `grid_view`), la misma
 * tabla que usa la sidebar de desktop.
 */
const TAB_PLANTILLAS: Tab = {
  to: '/interfaz-v2/plantillas',
  label: 'Plantillas',
  icono: 'dashboard_customize',
};
const TAB_CATALOGO: Tab = {
  to: '/interfaz-v2/modo/catalogo',
  label: 'Catálogo',
  icono: 'grid_view',
};
const TABS_DERECHA: Tab[] = [
  { to: '/redes-sociales/imagenes', label: 'Creaciones', icono: 'photo_library' },
  { to: '/mi-marca', label: 'Marca', icono: 'shopping_bag' },
];

/** Un destino: ícono + etiqueta, con el estado activo de la app (azul + relleno). */
function TabNav({ tab }: { tab: Tab }) {
  return (
    <NavLink
      to={tab.to}
      end
      className="flex flex-1 flex-col items-center justify-center gap-1 active:opacity-60"
    >
      {({ isActive }) => {
        // El estado lo marca el COLOR y nada más —azul de marca contra el gris—,
        // que es como lo marca la sidebar de desktop
        // (ADR-2026-08-04-los-iconos-de-la-barra-son-los-de-la-sidebar).
        //
        // El glifo se dibuja con los ajustes de la familia (`FILL 1, wght 400`), o
        // sea igual que allá: sin `.bottomnav-icon`, que los adelgazaba a `FILL 0,
        // wght 300`. Era la diferencia que quedaba: el mismo nombre de ícono, pero
        // de contorno fino en la barra y relleno en la sidebar.
        // El gris de los no seleccionados es el MISMO del shell de desktop
        // (`GRIS_SHELL`, negro al 60%) y no un `brand-black/45` propio. Además de
        // ser otro valor, `brand-black` no declara `<alpha-value>`: con modificador
        // de opacidad no emite regla y el color terminaba siendo el heredado.
        const color = isActive ? 'text-brand-primary' : 'text-black/60';
        return (
          <>
            <span
              className={`material-symbols-rounded text-[24px] leading-none transition-colors ${color}`}
            >
              {tab.icono}
            </span>
            <span className={`text-[11px] font-semibold leading-none transition-colors ${color}`}>
              {tab.label}
            </span>
          </>
        );
      }}
    </NavLink>
  );
}

export function NavInferiorMobile({
  onGenerar,
  conPlantillas,
}: {
  onGenerar: () => void;
  /**
   * `true` cuando la marca activa es de Moda. Entra por prop y no se resuelve acá:
   * el rubro ya lo tiene la home, que es quien monta esta barra, y con dos consultas
   * distintas la barra podría decir una cosa y la home otra. Al cambiar de marca la
   * home se re-renderiza y el lugar cambia solo.
   */
  conPlantillas: boolean;
}) {
  // "Modo tarea": la pantalla montó su propia barra de acción abajo (el Generar fijo
  // del paso Configurar, el CTA de selección de productos) o abrió un sub-flujo con
  // su propio back. La barra se desliza hacia abajo en vez de desmontarse de golpe,
  // igual que hacía `MobileBottomNav` — ADR-2026-08-04-el-modo-se-lleva-la-barra.
  const { mobileHideBottomNav } = useSidebar();
  const tabsIzquierda: Tab[] = [
    TAB_INICIO,
    conPlantillas ? TAB_PLANTILLAS : TAB_CATALOGO,
  ];
  return (
    <nav
      aria-hidden={mobileHideBottomNav}
      // Fondo blanco y opaco: la barra es una superficie, no un velo. Era
      // `#FAFAFA/95` —el gris del lienzo— y contra el fondo de la app, que desde
      // ADR-2026-08-04-mobile-usa-el-gris-de-desktop es ese mismo gris, no se
      // distinguía de la pantalla. El blanco la despega; el borde de arriba deja de
      // ser lo único que la separa del contenido.
      //
      // `md:hidden` y no `sm:hidden`: el shell mobile se monta por debajo de 1024px y
      // de 768 para arriba muestra la sidebar de escritorio, que es exactamente el
      // rango que cubría la barra vieja. Con `sm:` quedaba una franja de 640 a 768
      // sin ninguna navegación a la vista.
      className={`fixed inset-x-0 bottom-0 z-40 border-t border-[#F4F4F5] bg-white md:hidden transition-[transform,opacity] duration-300 ease-out ${
        mobileHideBottomNav
          ? 'translate-y-full opacity-0 pointer-events-none'
          : 'translate-y-0 opacity-100'
      }`}
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        // Al mantener apretado, iOS ya no ofrece el menú de selección
        // (Copy/Look Up) sobre los íconos. El feedback de "pressed" lo da el
        // `active:opacity-60` de cada lugar. Mismo tratamiento que la barra vieja.
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none',
      }}
    >
      {/* 64px de contenido + el safe area de abajo. En el diseño la barra mide 86,
          pero esos 22px de más son la franja del indicador de home de iOS, que acá
          la pone `env(safe-area-inset-bottom)` y en un Android sin barra de gestos
          no debería existir. */}
      <div className="flex h-16 items-stretch">
        {tabsIzquierda.map((t) => (
          <TabNav key={t.to} tab={t} />
        ))}

        {/* Botón central. Es un `button` y no un `NavLink` a propósito: abre el
            sheet sobre la pantalla en la que estés, sin cambiar la URL, así el
            back del sistema no queda cargado con un paso intermedio.

            El círculo va posicionado y no en el flujo de la columna: tiene que
            SALIR por arriba del borde de la barra (27px, la proporción del
            diseño) mientras su etiqueta se mantiene en la misma línea de base que
            las otras cuatro. Con márgenes negativos, cualquier cambio de alto de
            la barra desalineaba una de las dos cosas. */}
        <button
          type="button"
          onClick={onGenerar}
          aria-label="Generar"
          className="relative flex-1 active:opacity-60"
        >
          {/* 56px y no 64 — ADR-2026-08-04-el-boton-de-generar-se-afina. El `bottom`
              sube de 27 a 30px para que entre el círculo y su etiqueta queden 8px: la
              etiqueta tiene `leading-none`, así que su borde superior está a 22px del
              piso de la barra (11 de `bottom` + 11 de alto). El círculo asoma 22px por
              arriba del borde de la barra.

              FONDO: el degradé azul del botón "Generar imagen" del onboarding —
              ADR-2026-08-20-el-generar-de-la-barra-usa-el-azul-del-onboarding. La clase
              va copiada CARÁCTER POR CARÁCTER de `AccountSetupProduct.tsx`, y no es
              cosmética la insistencia: Tailwind genera las clases arbitrarias escaneando
              el texto de los fuentes, así que una variante equivalente pero escrita
              distinto (otro orden, otros decimales) sería una regla NUEVA — y en este
              repo hay clases arbitrarias que no llegan al CSS. Verificado contra
              `dist/assets/*.css`: el degradé ya está ahí.

              Acá había además `bg-[#D6F8FB]` (celeste claro) y un aro `ring-1
              ring-[#1644EB]/25`. El aro se va con el celeste: existía para despegar un
              círculo claro del blanco de la barra, y sobre el azul saturado es azul al
              25% sobre azul — invisible. El botón del onboarding tampoco lo tiene. */}
          <span className="absolute bottom-[30px] left-1/2 grid h-14 w-14 -translate-x-1/2 place-items-center rounded-full bg-[linear-gradient(181.83deg,#2563F5_8.76%,#0436AE_90.56%)]">
            {/* `auto_awesome` es el mismo glifo de chispa del diseño, y el que ya
                usa el modo automático en la sidebar. Sin clase de variación: los
                ajustes de la familia (`FILL 1, wght 400`) son los de la sidebar.

                BLANCO y ya no `text-brand-primary`: el azul de marca sobre el degradé
                azul no se veía. Es el mismo `#FAFAFA` que usa el botón del onboarding
                para su ícono y su texto, no un blanco puro. */}
            <span className="material-symbols-rounded text-[28px] leading-none text-[#FAFAFA]">
              auto_awesome
            </span>
          </span>
          <span className="absolute bottom-[11px] left-1/2 -translate-x-1/2 text-[11px] font-semibold leading-none text-black/60">
            Generar
          </span>
        </button>

        {TABS_DERECHA.map((t) => (
          <TabNav key={t.to} tab={t} />
        ))}
      </div>
    </nav>
  );
}
